import React, { useCallback } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';

import { makeStyles } from 'tss-react/mui';

import CurrencyTextField from 'components/CurrencyTextField';
import ExpandableCard from 'components/ExpandableCard';
import HighlightableTypography from 'components/HighlightableTypography';

import { useField } from 'app-components/flux/transaction-rules/transactionRulesFormHooks';
import type { AmountSourceMatchingType, AmountType } from 'app-components/flux/transaction-rules/transactionRulesTypes';

const useStyles = makeStyles()((theme) => ({
  row: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  column: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  flex0: {
    flex: '0 0 auto',
  },
  card: {
    border: `1px ${theme.palette.border.light} solid`,
    background: theme.palette.background.default,
    '& .MuiCardHeader-root': {
      height: 52,
      borderBottom: `1px solid ${theme.palette.border.light}`,
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
    },
    marginTop: 0,
  },
  range: {
    display: 'flex',
    width: '100%',
    '.MuiFormControl-root': {
      '&:first-of-type': {
        '.MuiInputBase-root': {
          borderRight: 0,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        },
      },
      '&:last-of-type': {
        '.MuiInputBase-root': {
          borderLeft: 1,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        },
      },
    },
  },
  primaryHoverOutline: {
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

type DateRange = {
  start: string;
  end: string;
};

type DollarRangeProps = {
  id: string;
  start?: number;
  end?: number;
  onChange: (value: Partial<DateRange>) => void;
  errorStart?: boolean;
  errorEnd?: boolean;
  showGreen?: boolean;
};

function DollarRangeInput({ id, start, end, onChange, errorStart, errorEnd, showGreen }: DollarRangeProps) {
  const { classes } = useStyles();

  const handleChange = (field: 'start' | 'end', value?: number) => {
    onChange({ [field]: value });
  };

  const prefix = showGreen ? '$ +' : '$';

  return (
    <Box className={classes.range}>
      <CurrencyTextField
        id={`${id}-start`}
        className={classes.primaryHoverOutline}
        placeholder={`${prefix} Range start`}
        value={start}
        onChange={(value) => handleChange('start', value)}
        error={!!errorStart}
        showGreen={showGreen}
      />
      <CurrencyTextField
        id={`${id}-end`}
        className={classes.primaryHoverOutline}
        placeholder={`${prefix} Range end`}
        value={end}
        onChange={(value) => handleChange('end', value)}
        error={!!errorEnd}
        showGreen={showGreen}
      />
    </Box>
  );
}

function AmountTarget() {
  const { cx, classes } = useStyles();
  const { value: type = 'EXPENSE', onValueChange: onTypeValueChange } = useField('amountSourceType');
  const { value: matchOperator, onValueChange: onMatchOperatorChange } = useField('amountSourceMatchingOperator');
  const { value: amount1, onValueChange: onAmount1Change, error: amount1Error } = useField('amountSourceAmount1');
  const { value: amount2, onValueChange: onAmount2Change, error: amount2Error } = useField('amountSourceAmount2');

  const onRangeChange = useCallback(
    (value: DateRange) => {
      value.start && onAmount1Change(Number(value.start));
      value.end && onAmount2Change(Number(value.end));
    },
    [onAmount1Change, onAmount2Change],
  );

  const showGreen = type === 'INCOME' && ((amount1 ?? 0) > 0 || (amount2 ?? 0) > 0);

  return (
    <Box className={classes.row}>
      <Box key={type} className={cx(classes.column, classes.flex0)} sx={{ flex: 0 }}>
        <FormControl component='fieldset'>
          <RadioGroup
            aria-label='options'
            name='options'
            value={type}
            onChange={(_event, value) => {
              onTypeValueChange(value as AmountType);
            }}
            sx={{ gap: 0.5 }}
          >
            <FormControlLabel
              id='amount-source-type-expense'
              value='EXPENSE'
              control={<Radio sx={{ py: 0 }} />}
              label={
                <HighlightableTypography value={type} match='EXPENSE'>
                  Expense
                </HighlightableTypography>
              }
            />
            <FormControlLabel
              id='amount-source-type-income'
              value='INCOME'
              control={<Radio sx={{ py: 0 }} />}
              label={
                <HighlightableTypography value={type} match='INCOME'>
                  Income
                </HighlightableTypography>
              }
            />
          </RadioGroup>
        </FormControl>
      </Box>
      <Box sx={{ width: '150px' }}>
        <FormControl id='amount-source-match-type' fullWidth>
          <Select
            labelId='comparison-select-label'
            value={matchOperator}
            className={classes.primaryHoverOutline}
            onChange={(e) => onMatchOperatorChange(e.target.value as AmountSourceMatchingType)}
          >
            <MenuItem value='EQUALS'>Equals</MenuItem>
            <MenuItem value='BETWEEN'>Between</MenuItem>
            <MenuItem value='GREATER_THAN'>Greater than</MenuItem>
            <MenuItem value='LESS_THAN'>Less than</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box className={classes.row}>
        {matchOperator !== 'BETWEEN' && (
          <CurrencyTextField
            id='amount1-source'
            className={classes.primaryHoverOutline}
            placeholder={showGreen ? '$ +0.00' : '$ 0.00'}
            value={amount1}
            onChange={onAmount1Change}
            error={!!amount1Error}
            showGreen={showGreen}
          />
        )}
        {matchOperator === 'BETWEEN' && (
          <DollarRangeInput
            id='amountRange-source'
            start={amount1}
            end={amount2}
            onChange={onRangeChange}
            errorStart={amount1Error}
            errorEnd={amount2Error}
            showGreen={showGreen}
          ></DollarRangeInput>
        )}
      </Box>
    </Box>
  );
}

export function AmountSourceCard(props: any) {
  const { classes, theme } = useStyles();
  const { value, onValueChange } = useField('isEnabledAmountSource');

  return (
    <ExpandableCard
      id='rule-amount-source'
      className={classes.card}
      title='Amount'
      cardHeaderProps={{ padding: `${theme.spacing(1.5)} ${theme.spacing(2)}` }}
      titleProps={{ variant: 'subtitle2' }}
      expandIcon={
        <Checkbox id='rule-amount-source-enabled' checked={value} onChange={(_, checked) => onValueChange(checked)} />
      }
      isExpanded={value || !!props.hideToggle}
      hideToggle={!!props.hideToggle}
      onExpandedChange={onValueChange}
    >
      <AmountTarget />
    </ExpandableCard>
  );
}
